import { map } from 'rxjs';
import { getRoute } from '@/app/core/api/routes';
import {
  UserFiltersInterface,
  UserCreatePayloadInterface,
  UserUpdatePayloadInterface,
  UserUpdatePasswordPayloadInterface,
  UserSocialLinksUpdatePayloadInterface,
} from '@/app/features/users';

import { Params } from '@angular/router';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { UserInterface } from '@/app/shared/interfaces/user.interface';
import { ResponseInterface } from '@/app/shared/interfaces/response.interface';

@Injectable({
  providedIn: 'root',
})
export class UsersApiService {
  constructor(private http: HttpClient) {}

  loadUsers(params: Params = {}) {
    const httpParams = new HttpParams({
      fromObject: params,
    });

    const action = getRoute('admin.users.list');
    return this.http.get<ResponseInterface<UserInterface[]>>(action, {
      params: httpParams,
    });
  }

  loadUser(userId: number) {
    const action = getRoute('admin.users.one', {
      id: userId,
    });

    return this.http.get<ResponseInterface<UserInterface>>(action);
  }

  createUser(payload: UserCreatePayloadInterface) {
    const action = getRoute('admin.users.list');

    return this.http.post<ResponseInterface<UserInterface>>(action, payload);
  }

  updateUser(id: number, payload: UserUpdatePayloadInterface | UserUpdatePasswordPayloadInterface) {
    const action = getRoute('admin.users.one', {
      id,
    });

    return this.http.put<ResponseInterface<UserInterface>>(action, payload);
  }

  updateUserAvatar(id: number, avatar: File | null = null) {
    const action = getRoute('admin.users.one.avatar', {
      id,
    });

    const payload = new FormData();
    if (!!avatar) {
      payload.append('file', avatar);
    }

    return this.http.post<ResponseInterface<UserInterface>>(action, payload);
  }

  updateSocialLinks(id: number, socials: UserSocialLinksUpdatePayloadInterface[]) {
    const action = getRoute('admin.users.one.socials', {
      id,
    });

    return this.http.post<ResponseInterface<UserInterface>>(action, {
      social_links: socials,
    });
  }

  bulkDeleteUsers(params: Params = {}) {
    const action = getRoute('admin.users.list');

    return this.http.delete<ResponseInterface<null>>(action, {
      params: new HttpParams({
        fromObject: params,
      }),
    });
  }

  deleteUser(id: number) {
    const action = getRoute('admin.users.one', {
      id,
    });

    return this.http.delete<ResponseInterface<UserInterface>>(action);
  }

  loadFilters(params: Params = {}) {
    const action = getRoute('admin.users.filters');

    return this.http.get<ResponseInterface<UserFiltersInterface>>(action, {
      params: new HttpParams({
        fromObject: params,
      }),
    });
  }

  exportUsers(params: Params = {}) {
    const action = getRoute('admin.users.export');

    return this.http
      .get(action, {
        observe: 'response',
        responseType: 'blob' as 'json',
        params: new HttpParams({
          fromObject: params,
        }),
      })
      .pipe(
        map((response) => {
          return {
            blob: response.body,
            contentDisposition: response.headers?.get('Content-Disposition'),
            contentType: response.headers.get('Content-Type'),
          };
        }),
      );
  }
}
